/**
 * Times for Schedule send, read and shown in the browser's time zone: the
 * agent picks "8:00 AM" on their own clock, and the server stores the instant.
 */

import type { ApolloClient } from '@apollo/client';
import { messageFromError } from '@oysterjs/core/graphql/errors';

import {
  CancelMailboxEmailDocument,
  type CancelMailboxEmailMutation,
  type CancelMailboxEmailMutationVariables,
  type MailboxProvider,
  OutboundEmailStatus,
  RescheduleMailboxEmailDocument,
  type RescheduleMailboxEmailMutation,
  type RescheduleMailboxEmailMutationVariables
} from '~/api/graphql';
import { formatTime } from './format-time';
import { mailClientName } from './mailbox-providers';

/** Mirrors mailbox.MinScheduleLead; the server refuses anything sooner. */
export const MIN_SCHEDULE_LEAD_MS = 5 * 60_000;

const QUICK_PICK_HOUR = 8;
const MONDAY = 1;
const DAYS_PER_WEEK = 7;

export type ScheduleQuickPick = {
  label: 'Tomorrow morning' | 'Monday morning';
  sendAt: Date;
};

const morningOf = (day: Date, daysAhead: number): Date =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate() + daysAhead, QUICK_PICK_HOUR);

const nextMondayMorning = (now: Date): Date => {
  const daysUntilMonday = (MONDAY - now.getDay() + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  const mondayMorning = morningOf(now, daysUntilMonday);
  const isTooSoon = mondayMorning.getTime() - now.getTime() < MIN_SCHEDULE_LEAD_MS;
  return isTooSoon ? morningOf(now, daysUntilMonday + DAYS_PER_WEEK) : mondayMorning;
};

export const tomorrowMorningOf = (now: Date): Date => morningOf(now, 1);

/**
 * The one-click send times in the Send menu. On a Sunday both name the same
 * morning, so only "Tomorrow morning" is offered.
 */
export const scheduleQuickPicks = (now: Date): ScheduleQuickPick[] => {
  const tomorrowMorning = tomorrowMorningOf(now);
  const mondayMorning = nextMondayMorning(now);
  const isSameMorning = tomorrowMorning.getTime() === mondayMorning.getTime();
  return isSameMorning
    ? [{ label: 'Tomorrow morning', sendAt: tomorrowMorning }]
    : [
        { label: 'Tomorrow morning', sendAt: tomorrowMorning },
        { label: 'Monday morning', sendAt: mondayMorning }
      ];
};

/**
 * Why the server would refuse this send time, in its words, so the dialog can
 * say so before the request. Null when the time is fine.
 */
export const pickedSendTimeErrorMessage = (
  sendAt: Date,
  now: Date,
  /** The button that sends without scheduling, named when a time is too soon. */
  sendNowLabel = 'Send now',
  tooSoonMessage = `Pick a time at least 5 minutes from now. To send sooner, use ${sendNowLabel}.`
): string | null => {
  const leadMs = sendAt.getTime() - now.getTime();
  if (leadMs <= 0) {
    return 'That time has already passed. Pick a later time.';
  }
  if (leadMs < MIN_SCHEDULE_LEAD_MS) {
    return tooSoonMessage;
  }
  return null;
};

const datePartReader = (date: Date, options: Intl.DateTimeFormatOptions, timeZone?: string) => {
  const parts = new Intl.DateTimeFormat('en-US', { ...options, timeZone }).formatToParts(date);
  return (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
};

/** "Tue Oct 6, 8:00 AM PDT": the full form, so the agent can check the day and the zone. */
export const formatSendTime = (date: Date, timeZone?: string): string => {
  const part = datePartReader(
    date,
    { weekday: 'short', month: 'short', day: 'numeric', timeZoneName: 'short' },
    timeZone
  );
  return `${part('weekday')} ${part('month')} ${part('day')}, ${formatTime(date, timeZone)} ${part('timeZoneName')}`;
};

/** "Tue Oct 6, 8:00 AM", for a menu that already names the zone once. */
export const formatSendTimeWithoutZone = (date: Date, timeZone?: string): string => {
  const part = datePartReader(date, { weekday: 'short', month: 'short', day: 'numeric' }, timeZone);
  return `${part('weekday')} ${part('month')} ${part('day')}, ${formatTime(date, timeZone)}`;
};

/**
 * The time alone when it falls on the same day as `reference`, otherwise with
 * its day, so a send held for days doesn't read as the same day.
 */
export const formatTimeOmittingSameDay = (date: Date, reference: Date): string =>
  date.toDateString() === reference.toDateString()
    ? formatTime(date)
    : formatSendTimeWithoutZone(date);

/** "Pacific Time (PDT)" */
export const timeZoneDescription = (date: Date, timeZone?: string): string => {
  const name = datePartReader(date, { timeZoneName: 'longGeneric' }, timeZone)('timeZoneName');
  const abbreviation = datePartReader(date, { timeZoneName: 'short' }, timeZone)('timeZoneName');
  return name === abbreviation ? name : `${name} (${abbreviation})`;
};

const pad = (value: number) => String(value).padStart(2, '0');

/** The value a date `<input>` shows for this instant, in the browser's zone. */
export const toDateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** The value a time `<input>` shows for this instant, in the browser's zone. */
export const toTimeInputValue = (date: Date): string =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

/**
 * The instant a date and time input name together, read in the browser's zone,
 * or null while either is incomplete.
 */
export const fromDateTimeInputs = (dateValue: string, timeValue: string): Date | null => {
  const [year, month, day] = dateValue.split('-').map(Number);
  const [hours, minutes] = timeValue.split(':').map(Number);
  const values = [year, month, day, hours, minutes];
  if (values.some((value) => value === undefined || Number.isNaN(value))) {
    return null;
  }
  return new Date(year, month - 1, day, hours, minutes);
};

// setTimeout fires at once for a delay longer than this.
export const LONGEST_TIMER_DELAY_MS = 2 ** 31 - 1;

/** Why Reschedule and Send now are off on a scheduled email while scheduled sending is off. */
export const RESCHEDULE_UNAVAILABLE_TITLE = "Changing the send time isn't available right now.";

/** What a scheduled email's send does to the draft linked in the agent's mail client. */
export const scheduledEmailHelper = (provider?: MailboxProvider): string =>
  `At the send time, Cara sends its latest version. Edits made to the ${provider ? mailClientName(provider) : 'mailbox'} draft are replaced.`;

/** Mirrors cara.ScheduledEmailLockedError, the server's refusal of an edit to someone else's scheduled email. */
export const scheduledByAnotherUserMessage = (schedulerName?: string | null): string => {
  const name = schedulerName?.trim();
  if (!name) return 'Someone else scheduled this email. Only they can change it.';
  return `${name} scheduled this email. Only they can change it.`;
};

const CHANGE_UNCONFIRMED = "Couldn't confirm that change. Check Outgoing.";
const CANCEL_REFUSED = 'We could not cancel this schedule. It may already be sending.';

/** The email as the server left it, or the server's refusal in its own words. */
type ScheduledEmailChange<Email> =
  | { email: Email; refusal: null }
  | { email: null; refusal: string };

type MutatingClient = Pick<ApolloClient<object>, 'mutate'>;

/** Moves a scheduled email to `sendAt`, or sends it now when null. Never throws. */
export const rescheduleScheduledEmail = async (
  client: MutatingClient,
  id: string,
  sendAt: Date | null
): Promise<ScheduledEmailChange<RescheduleMailboxEmailMutation['rescheduleMailboxEmail']>> => {
  try {
    const result = await client.mutate<
      RescheduleMailboxEmailMutation,
      RescheduleMailboxEmailMutationVariables
    >({
      mutation: RescheduleMailboxEmailDocument,
      variables: { id, input: sendAt ? { sendAt } : {} }
    });
    const moved = result.data?.rescheduleMailboxEmail;
    return moved
      ? { email: moved, refusal: null }
      : {
          email: null,
          refusal: messageFromError({ graphQLErrors: result.errors ?? [] }, CHANGE_UNCONFIRMED)
        };
  } catch (err) {
    return { email: null, refusal: messageFromError(err, CHANGE_UNCONFIRMED) };
  }
};

/**
 * Cancels a scheduled email; it becomes a normal draft again, and its linked
 * mailbox draft, if any, stays as it is. Never throws.
 */
export const cancelScheduledEmail = async (
  client: MutatingClient,
  id: string
): Promise<ScheduledEmailChange<CancelMailboxEmailMutation['cancelMailboxEmail']>> => {
  try {
    const result = await client.mutate<
      CancelMailboxEmailMutation,
      CancelMailboxEmailMutationVariables
    >({ mutation: CancelMailboxEmailDocument, variables: { id } });
    const cancelled = result.data?.cancelMailboxEmail;
    return cancelled?.status === OutboundEmailStatus.Cancelled
      ? { email: cancelled, refusal: null }
      : {
          email: null,
          refusal: messageFromError({ graphQLErrors: result.errors ?? [] }, CANCEL_REFUSED)
        };
  } catch (err) {
    return { email: null, refusal: messageFromError(err, CHANGE_UNCONFIRMED) };
  }
};
